import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { XCircle, Plus, X, Upload, Loader2 } from 'lucide-react';
import { API, resolveImg, getToken } from './shared';
import { uploadImages, describeFailures } from '../../lib/uploadImage';
import { formatIndianNumber, parseNumber } from '../../lib/indianFormat';

const AMENITIES_OPTIONS = [
  'parking', 'security', 'gym', 'ac', 'garden', 'waterfront', 'sauna', 'steam',
  'pool', 'intercom', 'clubhouse', 'meeting_room', 'furnished', 'semi_furnished',
  'equipped_kitchen', 'solar_power', 'heater', 'fan', 'electrical_provisions',
  'vastu_compliant', 'badminton_court', 'basketball_court', 'yoga', 'ev_charging',
  'childrens_play_area', 'basement_parking', 'smart_home', 'amphitheater', 'jogging_track',
  'reflexology_path', 'drivers_room', 'terrace', 'kids_pool', 'tennis_court',
  'pickleball_court', 'wall_climbing', 'jacuzzi', 'paddle_court'
];

const amenityLabel = (id) => id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const EditListingModal = ({ listing, onClose, onRefresh }) => {
  const [editForm, setEditForm] = useState({});
  const [editImages, setEditImages] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    if (listing) {
      setEditForm({
        title: listing.title || '',
        description: listing.description || '',
        price: listing.price || '',
        city: listing.city || '',
        locality: listing.locality || '',
        property_type: listing.property_type || '',
        bedrooms: listing.bedrooms || '',
        bathrooms: listing.bathrooms || '',
        size_sqft: listing.size_sqft || '',
        listing_type: listing.listing_type || 'buy',
        property_status: listing.property_status || 'ready',
        price_onwards: listing.price_onwards || false,
        new_launch: listing.new_launch || false,
        seller_name: listing.seller_name || '',
        seller_phone: listing.seller_phone || '',
        seller_email: listing.seller_email || '',
        seller_type: listing.seller_type || 'owner',
        rera_number: listing.rera_number || '',
        rera_link: listing.rera_link || '',
        location_link: listing.location_link || '',
        rera_images: listing.rera_images || [],
        land_size: listing.land_size || '',
        connectivity: { railway_station: '', hospital: '', police_station: '', school: '', mall: '', airport: '', ...(listing.connectivity || {}) },
        amenities: listing.amenities || [],
        size_sqft_onwards: listing.size_sqft_onwards || false,
        bedrooms_onwards: listing.bedrooms_onwards || false,
        unit_types: listing.unit_types || [],
        show_price_per_sqft: listing.show_price_per_sqft || false,
        price_per_cent: listing.price_per_cent || '',
        rent_period: listing.rent_period || 'per_month',
      });
      setEditImages((listing.images || []).map((url, i) => ({ url, id: `img-${i}` })));
    }
  }, [listing]);

  const handleImageReorder = (fromIdx, toIdx) => {
    const updated = [...editImages];
    const [moved] = updated.splice(fromIdx, 1);
    updated.splice(toIdx, 0, moved);
    setEditImages(updated);
  };

  const handleImageRemove = (idx) => {
    setEditImages(editImages.filter((_, i) => i !== idx));
  };

  const handleAddImages = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingImages(true);
    try {
      const { uploaded, failed } = await uploadImages(API, files);
      for (const item of uploaded) {
        setEditImages(prev => [...prev, { url: item.url, id: `img-new-${Date.now()}-${Math.random()}` }]);
      }
      if (failed.length) {
        alert(`${uploaded.length} of ${files.length} images uploaded successfully.\n\nFailed:\n${describeFailures(failed)}\n\nThe successful images have been added.`);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        price: editForm.price ? parseFloat(editForm.price) : 0,
        bedrooms: editForm.bedrooms ? parseFloat(editForm.bedrooms) : null,
        bathrooms: editForm.bathrooms ? parseInt(editForm.bathrooms) : null,
        size_sqft: editForm.size_sqft ? parseFloat(editForm.size_sqft) : null,
        land_size: editForm.land_size || null,
        seller_email: editForm.seller_email || null,
        rera_number: editForm.rera_number || null,
        rera_images: (editForm.rera_images || []).length > 0 ? editForm.rera_images : null,
        rera_link: editForm.rera_link || null,
        location_link: editForm.location_link || null,
        connectivity: Object.values(editForm.connectivity || {}).some(v => v) ? editForm.connectivity : null,
        new_launch: editForm.new_launch || false,
        price_onwards: editForm.price_onwards || false,
        images: editImages.map(img => img.url),
        amenities: editForm.amenities || [],
        size_sqft_onwards: editForm.size_sqft_onwards || false,
        bedrooms_onwards: editForm.bedrooms_onwards || false,
        unit_types: (editForm.unit_types || []).length > 0 ? editForm.unit_types : null,
        show_price_per_sqft: editForm.show_price_per_sqft || false,
        price_per_cent: editForm.price_per_cent ? parseFloat(editForm.price_per_cent) : null,
        rent_period: editForm.listing_type === 'rent' ? (editForm.rent_period || 'per_month') : null,
      };
      await axios.put(`${API}/admin/listings/${listing.id}`, payload, { headers: { Authorization: `Bearer ${getToken()}` } });
      onClose();
      onRefresh();
    } catch (error) {
      console.error('Error saving edit:', error);
      const detail = error.response?.data?.detail;
      let msg = 'Failed to save changes';
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(e => e.msg || JSON.stringify(e)).join('\n');
      alert(msg);
    } finally { setSavingEdit(false); }
  };

  if (!listing) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-8" onClick={e => e.stopPropagation()} data-testid="edit-listing-modal">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Edit Listing</h2>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><XCircle className="w-5 h-5" /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
            <input type="text" value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-title" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})} className="w-full rounded-lg border-slate-200 px-4 py-3 min-h-[100px]" data-testid="edit-description" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Price</label>
            <input type="text" inputMode="numeric" value={formatIndianNumber(editForm.price)} onChange={e => setEditForm({...editForm, price: parseNumber(e.target.value)})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-price" />
            <label className="flex items-center gap-2 mt-2 cursor-pointer">
              <input type="checkbox" checked={editForm.price_onwards || false} onChange={e => setEditForm({...editForm, price_onwards: e.target.checked})} className="w-4 h-4 rounded border-slate-300 accent-blue-600" data-testid="edit-price-onwards" />
              <span className="text-sm text-slate-600">Show as "Onwards"</span>
            </label>
          </div>
          <div>
            <label className="flex items-center gap-2 cursor-pointer h-11">
              <input type="checkbox" checked={editForm.show_price_per_sqft || false} onChange={e => setEditForm({...editForm, show_price_per_sqft: e.target.checked})} className="w-4 h-4 rounded border-slate-300 accent-blue-600" data-testid="edit-show-price-per-sqft" />
              <span className="text-sm text-slate-700">Display price per sq ft</span>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Price per Cent</label>
            <input type="text" inputMode="numeric" value={formatIndianNumber(editForm.price_per_cent)} onChange={e => setEditForm({...editForm, price_per_cent: parseNumber(e.target.value)})} className="w-full h-11 rounded-lg border-slate-200 px-4" placeholder="Optional" data-testid="edit-price-per-cent" />
          </div>
          {editForm.listing_type === 'rent' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rent Period</label>
              <div className="flex gap-2">
                <button type="button" onClick={() => setEditForm({...editForm, rent_period: 'per_month'})} className={`flex-1 h-11 rounded-lg text-sm font-medium border-2 transition-all ${editForm.rent_period === 'per_month' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`} data-testid="edit-rent-per-month">Per Month</button>
                <button type="button" onClick={() => setEditForm({...editForm, rent_period: 'per_day'})} className={`flex-1 h-11 rounded-lg text-sm font-medium border-2 transition-all ${editForm.rent_period === 'per_day' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600'}`} data-testid="edit-rent-per-day">Per Day</button>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Bedrooms</label>
            <input type="number" value={editForm.bedrooms} onChange={e => setEditForm({...editForm, bedrooms: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-bedrooms" />
            {(editForm.property_status === 'off_plan' || (editForm.unit_types || []).length > 0) && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={editForm.bedrooms_onwards} onChange={(e) => setEditForm({...editForm, bedrooms_onwards: e.target.checked})} className="w-4 h-4 rounded border-slate-300 accent-blue-600" data-testid="edit-bedrooms-onwards" />
                <span className="text-sm text-slate-600">Show as &quot;Onwards&quot;</span>
              </label>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Bathrooms</label>
            <input type="number" value={editForm.bathrooms} onChange={e => setEditForm({...editForm, bathrooms: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-bathrooms" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
            <input type="text" value={editForm.city} onChange={e => setEditForm({...editForm, city: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-city" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Locality</label>
            <input type="text" value={editForm.locality} onChange={e => setEditForm({...editForm, locality: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-locality" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Property Type</label>
            <select value={editForm.property_type} onChange={e => setEditForm({...editForm, property_type: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-property-type">
              <option value="apartment">Apartment</option>
              <option value="villa">Villa</option>
              <option value="land">Land/Plot</option>
              <option value="commercial">Commercial</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Listing Type</label>
            <select value={editForm.listing_type} onChange={e => setEditForm({...editForm, listing_type: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-listing-type">
              <option value="buy">Buy</option>
              <option value="rent">Rent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Size (sq.ft)</label>
            <input type="number" value={editForm.size_sqft} onChange={e => setEditForm({...editForm, size_sqft: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-size" />
            {(editForm.property_status === 'off_plan' || (editForm.unit_types || []).length > 0) && (
              <label className="flex items-center gap-2 mt-2 cursor-pointer">
                <input type="checkbox" checked={editForm.size_sqft_onwards} onChange={(e) => setEditForm({...editForm, size_sqft_onwards: e.target.checked})} className="w-4 h-4 rounded border-slate-300 accent-blue-600" data-testid="edit-sqft-onwards" />
                <span className="text-sm text-slate-600">Show as &quot;Onwards&quot;</span>
              </label>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
            <select value={editForm.property_status} onChange={e => setEditForm({...editForm, property_status: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-status">
              <option value="ready">Ready</option>
              <option value="off_plan">Off Plan</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Seller Name</label>
            <input type="text" value={editForm.seller_name} onChange={e => setEditForm({...editForm, seller_name: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-seller-name" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Seller Phone</label>
            <input type="text" value={editForm.seller_phone} onChange={e => setEditForm({...editForm, seller_phone: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-seller-phone" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Seller Email</label>
            <input type="email" value={editForm.seller_email} onChange={e => setEditForm({...editForm, seller_email: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" placeholder="Optional" data-testid="edit-seller-email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Seller Type</label>
            <select value={editForm.seller_type} onChange={e => setEditForm({...editForm, seller_type: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-seller-type">
              <option value="owner">Owner</option>
              <option value="agent">Agent</option>
              <option value="builder">Builder</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">RERA Number</label>
            <input type="text" value={editForm.rera_number} onChange={e => setEditForm({...editForm, rera_number: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" data-testid="edit-rera" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">RERA Link</label>
            <input type="url" value={editForm.rera_link || ''} onChange={e => setEditForm({...editForm, rera_link: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" placeholder="https://rera.kerala.gov.in/..." data-testid="edit-rera-link" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">Google Maps Location Link</label>
            <input type="url" value={editForm.location_link || ''} onChange={e => setEditForm({...editForm, location_link: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" placeholder="https://maps.google.com/..." data-testid="edit-location-link" />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-2">RERA Images (max 5)</label>
            <div className="flex flex-wrap gap-2">
              {(editForm.rera_images || []).map((img, idx) => (
                <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-slate-200">
                  <img src={resolveImg(img)} alt={`RERA ${idx + 1}`} className="w-full h-full object-cover" />
                  <button type="button" onClick={() => setEditForm({...editForm, rera_images: editForm.rera_images.filter((_, i) => i !== idx)})} className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-600 text-white rounded-full flex items-center justify-center">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {(editForm.rera_images || []).length < 5 && (
                <label htmlFor="edit-rera-file-input" className="w-20 h-20 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 hover:border-purple-400 cursor-pointer transition-all">
                  <input id="edit-rera-file-input" type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const fd = new FormData();
                      fd.append('file', file);
                      const res = await axios.post(`${API}/upload-image`, fd);
                      setEditForm(prev => ({...prev, rera_images: [...(prev.rera_images || []), res.data.url]}));
                    } catch (err) { console.error(err); alert('Upload failed'); }
                    e.target.value = '';
                  }} />
                  <Upload className="w-4 h-4 text-slate-400" />
                  <span className="text-[10px] text-slate-400">Add</span>
                </label>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Land Size</label>
            <input type="text" value={editForm.land_size} onChange={e => setEditForm({...editForm, land_size: e.target.value})} className="w-full h-11 rounded-lg border-slate-200 px-4" placeholder="e.g., 5 cents" data-testid="edit-land-size" />
          </div>
        </div>

        {/* Connectivity */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Connectivity (Distance to nearby facilities)</label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { key: 'railway_station', label: 'Railway Station' },
              { key: 'hospital', label: 'Hospital' },
              { key: 'police_station', label: 'Police Station' },
              { key: 'school', label: 'School' },
              { key: 'mall', label: 'Mall' },
              { key: 'airport', label: 'Airport' }
            ].map(({ key, label }) => {
              const valMap = { 'Under 5 km': 1, 'Under 10 km': 2, 'Under 15 km': 3, 'Above 15 km': 4 };
              const reverseMap = { 0: '', 1: 'Under 5 km', 2: 'Under 10 km', 3: 'Under 15 km', 4: 'Above 15 km' };
              const current = valMap[(editForm.connectivity || {})[key]] || 0;
              return (
                <div key={key} data-testid={`edit-connectivity-${key}`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-medium text-slate-700">{label}</span>
                    <span className="text-[10px] font-semibold text-blue-600">{(editForm.connectivity || {})[key] || 'Not set'}</span>
                  </div>
                  <input
                    type="range" min="0" max="4" step="1" value={current}
                    onChange={(e) => setEditForm({ ...editForm, connectivity: { ...(editForm.connectivity || {}), [key]: reverseMap[Number(e.target.value)] } })}
                    className="w-full h-1.5 bg-slate-200 rounded-full appearance-none cursor-pointer accent-blue-600"
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Unit Types */}
        {(editForm.unit_types || []).length > 0 || editForm.property_status === 'off_plan' ? (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="edit-unit-types">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-slate-800">Unit Configurations</h4>
              <button type="button" onClick={() => setEditForm({...editForm, unit_types: [...(editForm.unit_types || []), {bedrooms:'',price:'',sqft_min:'',sqft_max:'',bathrooms:''}]})}
                className="h-8 px-3 rounded-lg bg-amber-600 text-white text-xs font-medium hover:bg-amber-700 transition-all flex items-center gap-1" data-testid="edit-add-unit">
                <Plus className="w-3.5 h-3.5" /> Add
              </button>
            </div>
            {(editForm.unit_types || []).length === 0 && <p className="text-xs text-slate-400 text-center py-2">No configurations. Click Add to create.</p>}
            <div className="space-y-3">
              {(editForm.unit_types || []).map((unit, idx) => (
                <div key={idx} className="bg-white rounded-lg border border-slate-200 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase">Config {idx + 1}</span>
                    <button type="button" onClick={() => setEditForm({...editForm, unit_types: editForm.unit_types.filter((_,i) => i !== idx)})} className="text-red-500 hover:text-red-700">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Beds</label>
                      <select value={unit.bedrooms} onChange={(e) => { const u = [...editForm.unit_types]; u[idx] = {...u[idx], bedrooms: e.target.value}; setEditForm({...editForm, unit_types: u}); }}
                        className="w-full h-9 rounded-lg border-slate-200 px-2 text-sm">
                        <option value="">-</option>
                        {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Price (from)</label>
                      <input type="text" inputMode="numeric" placeholder="50,00,000" value={formatIndianNumber(unit.price)} onChange={(e) => { const u = [...editForm.unit_types]; u[idx] = {...u[idx], price: parseNumber(e.target.value)}; setEditForm({...editForm, unit_types: u}); }}
                        className="w-full h-9 rounded-lg border-slate-200 px-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Sqft Min</label>
                      <input type="number" placeholder="1200" value={unit.sqft_min} onChange={(e) => { const u = [...editForm.unit_types]; u[idx] = {...u[idx], sqft_min: e.target.value}; setEditForm({...editForm, unit_types: u}); }}
                        className="w-full h-9 rounded-lg border-slate-200 px-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Sqft Max</label>
                      <input type="number" placeholder="1800" value={unit.sqft_max} onChange={(e) => { const u = [...editForm.unit_types]; u[idx] = {...u[idx], sqft_max: e.target.value}; setEditForm({...editForm, unit_types: u}); }}
                        className="w-full h-9 rounded-lg border-slate-200 px-2 text-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] font-medium text-slate-500 block mb-0.5">Baths</label>
                      <select value={unit.bathrooms} onChange={(e) => { const u = [...editForm.unit_types]; u[idx] = {...u[idx], bathrooms: e.target.value}; setEditForm({...editForm, unit_types: u}); }}
                        className="w-full h-9 rounded-lg border-slate-200 px-2 text-sm">
                        <option value="">-</option>
                        {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Amenities */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Amenities</label>
          <div className="flex flex-wrap gap-2" data-testid="edit-amenities">
            {AMENITIES_OPTIONS.map((amenity) => {
              const isSelected = (editForm.amenities || []).includes(amenity);
              return (
                <button
                  key={amenity}
                  type="button"
                  onClick={() => {
                    const current = editForm.amenities || [];
                    setEditForm({
                      ...editForm,
                      amenities: isSelected
                        ? current.filter(a => a !== amenity)
                        : [...current, amenity]
                    });
                  }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                    isSelected
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                  data-testid={`edit-amenity-${amenity}`}
                >
                  {amenityLabel(amenity)}
                </button>
              );
            })}
          </div>
        </div>

        {/* Image Reordering + Upload */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-slate-700">Images ({editImages.length}) — Click to set cover or drag to reorder</label>
            <label className={`h-8 px-3 rounded-lg text-xs font-medium flex items-center gap-1.5 cursor-pointer transition-all ${uploadingImages ? 'bg-slate-100 text-slate-400' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`} data-testid="edit-add-images">
              {uploadingImages ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {uploadingImages ? 'Uploading...' : 'Add Images'}
              <input type="file" accept="image/*" multiple onChange={handleAddImages} className="hidden" disabled={uploadingImages} />
            </label>
          </div>
          {editImages.length > 0 ? (
            <div className="grid grid-cols-4 gap-3">
              {editImages.map((img, idx) => (
                <div
                  key={img.id}
                  draggable
                  onDragStart={e => e.dataTransfer.setData('text/plain', idx.toString())}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); handleImageReorder(parseInt(e.dataTransfer.getData('text/plain')), idx); }}
                  className={`relative group rounded-lg overflow-hidden h-28 bg-slate-100 cursor-grab border-2 ${idx === 0 ? 'border-blue-500' : 'border-transparent'}`}
                  data-testid={`edit-image-${idx}`}
                >
                  <img src={resolveImg(img.url)} alt={`Image ${idx + 1}`} className="w-full h-full object-cover" />
                  {idx === 0 && <span className="absolute top-1 left-1 text-[10px] font-bold bg-blue-600 text-white px-1.5 py-0.5 rounded">Cover</span>}
                  {idx !== 0 && (
                    <button onClick={() => handleImageReorder(idx, 0)} className="absolute bottom-1 left-1 text-[10px] font-semibold bg-white/90 text-slate-700 px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-blue-600 hover:text-white" data-testid={`edit-set-cover-${idx}`}>
                      Set as Cover
                    </button>
                  )}
                  <button onClick={() => handleImageRemove(idx)} className="absolute top-1 right-1 w-6 h-6 bg-red-600 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-700" data-testid={`edit-image-remove-${idx}`}>
                    <XCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-500">No images</p>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="h-11 px-6 rounded-full bg-slate-100 text-slate-700 font-medium hover:bg-slate-200 transition-all">Cancel</button>
          <button onClick={handleSaveEdit} disabled={savingEdit} className="h-11 px-8 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all disabled:opacity-50 flex items-center gap-2" data-testid="edit-save-button">
            {savingEdit ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditListingModal;
